import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, X, RotateCcw, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

const LivePhotoCapture = ({ onCapture, onClose, role = 'guest' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewMetadata, setPreviewMetadata] = useState(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [cameraReady, setCameraReady] = useState(false);

  const startCamera = useCallback(async (facing) => {
    try {
      // Stop existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      setCameraReady(false);
      setError(null);

      const constraints = {
        video: {
          facingMode: facing,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
        };
      }
    } catch (err) {
      console.error('Camera access error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError('Unable to access camera. Please try again.');
      }
    }
  }, []);

  useEffect(() => {
    startCamera(facingMode);
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, [startCamera, facingMode]);

  const switchCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    setCapturing(true);

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Generate timestamp
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const timestampText = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;

    // Draw timestamp overlay bar at bottom
    const barHeight = Math.max(40, canvas.height * 0.05);
    const fontSize = Math.max(16, barHeight * 0.5);

    // Semi-transparent dark bar
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, canvas.height - barHeight, canvas.width, barHeight);

    // Timestamp text - left side
    ctx.fillStyle = '#FFFFFF';
    ctx.font = `bold ${fontSize}px monospace`;
    ctx.textBaseline = 'middle';
    ctx.fillText(timestampText, 12, canvas.height - barHeight / 2);

    // "LIVE CAPTURE" badge - right side
    const badgeText = 'LIVE CAPTURE';
    const badgeWidth = ctx.measureText(badgeText).width;
    ctx.fillStyle = '#22C55E';
    ctx.font = `bold ${fontSize * 0.8}px monospace`;
    ctx.fillText(badgeText, canvas.width - badgeWidth - 20, canvas.height - barHeight / 2);

    // Red recording dot
    ctx.beginPath();
    ctx.arc(canvas.width - badgeWidth - 36, canvas.height - barHeight / 2, fontSize * 0.2, 0, Math.PI * 2);
    ctx.fillStyle = '#EF4444';
    ctx.fill();

    // Convert to blob
    canvas.toBlob((blob) => {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      const metadata = {
        capturedAt: now.toISOString(),
        isLiveCapture: true,
        deviceInfo: navigator.userAgent,
        timestampText: timestampText
      };

      setPreview(dataUrl);
      setPreviewBlob(blob);
      setPreviewMetadata(metadata);
      setCapturing(false);
    }, 'image/jpeg', 0.85);
  }, []);

  const handleRetake = () => {
    setPreview(null);
    setPreviewBlob(null);
    setPreviewMetadata(null);
  };

  const saveBlobToDevice = useCallback((blob, filename) => {
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (err) {
      console.error('Auto-save to device failed:', err);
    }
  }, []);

  const handleUsePhoto = () => {
    if (previewBlob && previewMetadata && onCapture) {
      // Auto-save to device
      const ts = previewMetadata.capturedAt ? new Date(previewMetadata.capturedAt) : new Date();
      const filename = `evidence-${ts.getFullYear()}${String(ts.getMonth()+1).padStart(2,'0')}${String(ts.getDate()).padStart(2,'0')}-${String(ts.getHours()).padStart(2,'0')}${String(ts.getMinutes()).padStart(2,'0')}${String(ts.getSeconds()).padStart(2,'0')}.jpg`;
      saveBlobToDevice(previewBlob, filename);

      onCapture(previewBlob, previewMetadata);
    }
    // Stop camera
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
  };

  const handleClose = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    if (onClose) onClose();
  };

  const roleLabels = {
    host: 'Capture evidence photo for your records',
    guest: 'Take a photo to protect yourself',
    provider: 'Capture photo for task evidence'
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80 z-10">
        <Button variant="ghost" size="sm" onClick={handleClose} className="text-white hover:bg-white/20">
          <X className="w-5 h-5" />
        </Button>
        <p className="text-white text-sm font-medium">{roleLabels[role] || 'Take a photo'}</p>
        <Button variant="ghost" size="sm" onClick={switchCamera} className="text-white hover:bg-white/20">
          <RotateCcw className="w-5 h-5" />
        </Button>
      </div>

      {/* Camera / Preview */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {error ? (
          <div className="text-center px-8">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <p className="text-white text-lg mb-2">Camera Error</p>
            <p className="text-gray-400 text-sm">{error}</p>
            <Button className="mt-4 bg-white text-black hover:bg-gray-200" onClick={() => startCamera(facingMode)}>
              Try Again
            </Button>
          </div>
        ) : preview ? (
          <img src={preview} alt="Captured" className="max-w-full max-h-full object-contain" />
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="max-w-full max-h-full object-contain"
            />
            {!cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-white border-t-transparent" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Controls */}
      <div className="bg-black/80 px-6 py-5">
        {preview ? (
          <div className="flex items-center justify-center gap-6">
            <Button
              onClick={handleRetake}
              variant="outline"
              className="border-white text-white hover:bg-white/20 px-6 py-2"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Retake
            </Button>
            <Button
              onClick={handleUsePhoto}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2"
            >
              <Check className="w-4 h-4 mr-2" /> Use Photo
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center">
            <button
              onClick={capturePhoto}
              disabled={!cameraReady || capturing}
              className="w-16 h-16 rounded-full border-4 border-white flex items-center justify-center
                         hover:bg-white/20 active:bg-white/40 transition-colors disabled:opacity-50"
            >
              <div className="w-12 h-12 rounded-full bg-white" />
            </button>
          </div>
        )}

        {!preview && role === 'host' && (
          <p className="text-yellow-400 text-xs text-center mt-3">
            Evidence photos are timestamped and cannot be edited
          </p>
        )}
        {!preview && role === 'guest' && (
          <p className="text-blue-400 text-xs text-center mt-3">
            Photos help protect you against false claims
          </p>
        )}
      </div>
    </div>
  );
};

export default LivePhotoCapture;
